import { Container, Graphics, Sprite, Texture } from 'pixi.js';
import type { AABB } from '../engine/Physics';
import { aabbOverlap } from '../engine/Physics';

const DUMMY_W = 32;
const DUMMY_H = 48;
const SHOOT_INTERVAL_MS = 500;
const GRAVITY = 0.6;
const MAX_FALL = 20;

export interface DummyBulletSpawn {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

export class Dummy {
  pos = { x: 0, y: 0 };
  alive = false;
  facing: 1 | -1 = 1;
  private vy = 0;
  onGround = false;
  readonly width = DUMMY_W;
  readonly height = DUMMY_H;

  view = new Container();
  private sprite: Sprite | null = null;
  hp = 5;
  readonly maxHp = 5;
  private shootTimer = 0;
  private hpBg = new Graphics();
  private hpFill = new Graphics();

  constructor(cucumberUrl: string) {
    const tex = Texture.from(cucumberUrl);
    this.sprite = new Sprite(tex);
    this.sprite.anchor.set(0.5);
    const targetH = DUMMY_H * 1.5;
    const aspect = tex.width / tex.height;
    this.sprite.height = targetH;
    this.sprite.width = targetH * aspect;
    // красноватый оттенок чтобы отличать от игрока
    this.view.addChild(this.sprite);
    this.view.addChild(this.hpBg);
    this.view.addChild(this.hpFill);
    this.view.visible = false;
  }

  bounds(): AABB {
    return { x: this.pos.x, y: this.pos.y, w: DUMMY_W, h: DUMMY_H };
  }

  spawn(x: number, y: number, facingDir: 1 | -1): void {
    this.pos.x = x;
    this.pos.y = y;
    this.facing = facingDir;
    this.alive = true;
    this.hp = this.maxHp;
    this.shootTimer = 0;
    this.view.visible = true;
    this.syncGfx();
  }

  kill(): void {
    this.alive = false;
    this.view.visible = false;
  }

  takeDamage(amount = 1): boolean {
    this.hp = Math.max(0, this.hp - amount);
    if (this.hp <= 0) { this.kill(); return true; }
    return false;
  }

  resetHp(): void { this.hp = this.maxHp; }

  // Физика — гравитация и коллизии с платформами
  physicsStep(solids: readonly AABB[]): void {
    if (!this.alive) return;
    this.vy = Math.min(this.vy + GRAVITY, MAX_FALL);
    this.pos.y += this.vy;
    this.onGround = false;

    const bb = this.bounds();
    for (const s of solids) {
      if (!aabbOverlap(bb, s)) continue;
      const overlapY = (bb.y + bb.h) - s.y;
      const overlapYTop = (s.y + s.h) - bb.y;
      if (overlapY < overlapYTop && this.vy >= 0) {
        this.pos.y -= overlapY;
        this.vy = 0;
        this.onGround = true;
      } else if (overlapYTop < overlapY) {
        this.pos.y += overlapYTop;
        this.vy = 0;
      }
    }
  }

  // Возвращает пулю если пора стрелять
  step(dtMs: number): DummyBulletSpawn | null {
    if (!this.alive) return null;
    this.shootTimer += dtMs;
    if (this.shootTimer < SHOOT_INTERVAL_MS) return null;
    this.shootTimer = 0;

    const muzzleX = this.pos.x + DUMMY_W / 2 + this.facing * (DUMMY_W / 2 + 4);
    const muzzleY = this.pos.y + DUMMY_H * 0.55;
    return { x: muzzleX, y: muzzleY, vx: this.facing * 12, vy: 0 };
  }

  syncGfx(): void {
    if (!this.sprite) return;
    this.view.x = this.pos.x + DUMMY_W / 2;
    this.view.y = this.pos.y + DUMMY_H / 2;
    this.sprite.scale.x = Math.abs(this.sprite.scale.x) * this.facing;
    // HP бар
    const bw = 36, bh = 4, bx = -bw/2, by = -DUMMY_H - 8;
    const frac = Math.max(0, this.hp / this.maxHp);
    const col = frac > 0.5 ? 0x44dd44 : frac > 0.25 ? 0xffcc00 : 0xff3333;
    this.hpBg.clear().rect(bx, by, bw, bh).fill({ color: 0x333333, alpha: 0.8 });
    this.hpFill.clear().rect(bx, by, bw * frac, bh).fill({ color: col });
  }
}
